import type { AutomationConfig } from "@smartsuite/automation-engine-api";
import { AutomationConfig_UserStatus } from "@smartsuite/automation-engine-api";
import { useState, useEffect } from "react";
import * as R from "remeda";
import { t } from "i18next";
import { ConfirmAutomationCard } from "../../../elements/ConfirmModal/ConfirmAutomationCard";
import { useRootStore } from "../../../stores/hooks";
import {
  EmptyContainer,
  StyledApplicationCount,
  StyledApplicationInfo,
  StyledAutomationCardList,
  StyledAutomationsTab,
  StyledAutomationsTabHeader,
} from "../style";
import { AutomationCard } from "./AutomationCard";
import { useEngineClient } from "../../../services/engineClient";
import { observer } from "mobx-react-lite";
import { IconWrapper } from "./style";
import { useNavigate } from "react-router-dom";
import {
  Badge,
  Icon,
  Paragraph,
  Preloader,
  TextSizes,
  Title,
} from "@smartsuite/react-ui/lib";
import { WHEN_BUTTON_IS_CLICKED_TRIGGER_ID } from "../../../utils/constants";

const storageKey = "expanded";

const setToStorage = (value: Record<string, boolean>): void => {
  localStorage.setItem(storageKey, JSON.stringify(value));
};

export const AutomationCards = observer<{
  appName: string;
  automations: AutomationConfig[];
  onClose?: () => void;
}>(({ appName, automations, onClose }) => {
  const navigate = useNavigate();
  const store = useRootStore();
  const engineClient = useEngineClient();
  const { applications } = store;
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [expanded, setExpanded] = useState(true);

  const onExpandedClick = (): void => {
    const expandedList = localStorage.getItem(storageKey);
    const parsedExpandedList: Record<string, boolean> = expandedList
      ? JSON.parse(expandedList)
      : {};
    setExpanded((value) => !value);
    setToStorage({
      ...parsedExpandedList,
      [appName]: !parsedExpandedList[appName],
    });
  };

  const editAutomation = (automation: AutomationConfig): void => {
    goAutomationForm(automation.automationId);
  };

  const goAutomationForm = (automationId: string): void => {
    navigate(`/${automationId}`);
  };

  const toggleStatus = (
    checked: boolean,
    automation: AutomationConfig,
  ): void => {
    const userStatus = checked
      ? AutomationConfig_UserStatus.USER_ENABLED
      : AutomationConfig_UserStatus.USER_DISABLED;
    const nextAutomation: AutomationConfig = R.clone({
      ...automation,
      userStatus,
    });
    setIsLoading(true);
    void engineClient
      .updateAutomation({ automation: nextAutomation })
      .then(({ lastUpdated, systemStatus }) => {
        automation.userStatus = userStatus; // TODO do we need to do it here?
        store.automations.updateItem({
          ...nextAutomation,
          lastUpdated,
          systemStatus: systemStatus ?? automation.systemStatus,
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const removeAutomation = (automationId: string): void => {
    store.modal.modal.isLoading = true;
    void engineClient
      .deleteAutomation(automationId)
      .then(() => store.automations.deleteItem(automationId))
      .finally(() => {
        setIsLoading(false);
        store.modal.closeConfirmModal();
        if (
          store.automations.getAutomationsWithValidTrigger(applications.list)
            .length === 0
        ) {
          onClose?.();
        }
      });
  };

  const onDelete = (automation: AutomationConfig): void => {
    if (automation.automationId !== "") {
      setIsLoading(true);
      store.modal.openConfirmModal({
        type: "warning",
        title:
          automation.trigger?.triggerReference?.triggerId ===
          WHEN_BUTTON_IS_CLICKED_TRIGGER_ID
            ? t("confirm-modal.delete-automation-button-title")
            : t("confirm-modal.delete-automation-title"),
        size: "M",
        body: (
          <ConfirmAutomationCard
            automation={automation}
            toggleStatus={toggleStatus}
          />
        ),
        negativeButtonText: t("confirm-modal.no-cancel"),
        positiveButtonText:
          automation.trigger?.triggerReference?.triggerId ===
          WHEN_BUTTON_IS_CLICKED_TRIGGER_ID
            ? t("confirm-modal.yes-delete-automation-button")
            : t("confirm-modal.yes-delete-automation"),
        onClose: () => {
          store.modal.closeConfirmModal();
          setIsLoading(false);
        },
        onConfirm: () => {
          removeAutomation(automation.automationId);
        },
      });
    }
  };

  useEffect(() => {
    const expandedList = localStorage.getItem(storageKey);
    const parsedExpandedList: Record<string, boolean> = expandedList
      ? JSON.parse(expandedList)
      : {};
    if (parsedExpandedList[appName] === undefined) {
      setToStorage({ ...parsedExpandedList, [appName]: true });
    } else {
      setExpanded(parsedExpandedList[appName]);
    }
  }, []);

  return (
    <StyledAutomationsTab>
      {isLoading && <Preloader />}

      <StyledAutomationsTabHeader onClick={onExpandedClick}>
        <IconWrapper expanded={expanded}>
          <Icon size="L" name="down-arrow" />
        </IconWrapper>

        <StyledApplicationInfo>
          <Title weight="bold" size="s">
            {appName}
          </Title>

          <StyledApplicationCount>
            <Badge
              styles={{
                height: 20,
                sidePadding: 7,
                textWeight: "medium",
                textSize: TextSizes.M,
              }}
            >
              {automations.length}
            </Badge>
          </StyledApplicationCount>
        </StyledApplicationInfo>
      </StyledAutomationsTabHeader>

      <StyledAutomationCardList>
        {expanded
          ? automations.map((automation) => {
              return (
                <AutomationCard
                  key={automation.automationId}
                  automation={automation}
                  onDelete={onDelete}
                  editAutomation={editAutomation}
                  toggleStatus={toggleStatus}
                />
              );
            })
          : null}
        {automations.length === 0 && (
          <EmptyContainer>
            <Paragraph align="left" size="m" color="secondary">
              {t("automation-list.no-automations")}
            </Paragraph>
          </EmptyContainer>
        )}
      </StyledAutomationCardList>
    </StyledAutomationsTab>
  );
});
